import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";

// ============================================================================
// COVERPAGE — Front/Back Cover PDF Composer
// Route: /cover
//
// Same idea as the Pagebind book composer, but built for cover pages:
// images sit edge-to-edge with ZERO gap between them and ZERO outer margin —
// a true full-bleed layout. There is no spacing control at all, because
// covers must never show a gap between photos or a white border at the trim.
// ============================================================================

// ---- Page trim sizes (mm) --------------------------------------------------
const PAGE_SIZES = {
  portrait:  { w: 305, h: 460, label: "305 × 460 mm Portrait" },
  landscape: { w: 460, h: 305, label: "460 × 305 mm Landscape" },
};

const PRINT_SIZE_PRESETS = [
  { name: "A4", w: 210, h: 297 },
  { name: "A5", w: 148, h: 210 },
  { name: "A6", w: 105, h: 148 },
];
const SAVED_PRINT_SIZES_KEY = "pitics-offset-saved-print-sizes-v1";

function calculatePrintFit(sheetW, sheetH, itemW, itemH) {
  const normal = Math.floor(sheetW / itemW) * Math.floor(sheetH / itemH);
  const rotated = Math.floor(sheetW / itemH) * Math.floor(sheetH / itemW);
  return Math.max(normal, rotated);
}

function PrintSizeControls({ sheetW, sheetH, perPage, setPerPage, maxCount = 100 }) {
  const [manualName, setManualName] = useState("");
  const [manualW, setManualW] = useState("");
  const [manualH, setManualH] = useState("");
  const [savedSizes, setSavedSizes] = useState([]);
  const [activeSize, setActiveSize] = useState("");

  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(SAVED_PRINT_SIZES_KEY) || "[]");
      if (Array.isArray(parsed)) setSavedSizes(parsed.filter((x) =>
        x && typeof x.name === "string" && Number(x.w) > 0 && Number(x.h) > 0
      ));
    } catch (_) { setSavedSizes([]); }
  }, []);

  const applySize = (size) => {
    const fit = calculatePrintFit(sheetW, sheetH, Number(size.w), Number(size.h));
    if (!fit) {
      alert("This print size is larger than the current sheet. No complete item can fit.");
      return;
    }
    const count = Math.min(maxCount, fit);
    setPerPage(count);
    setActiveSize(size.name);
  };

  const saveManualSize = () => {
    const name = manualName.trim();
    const w = Number(manualW);
    const h = Number(manualH);
    if (!name || !Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
      alert("Enter a name and valid width and height in millimetres.");
      return;
    }
    const next = [...savedSizes.filter((x) => x.name.toLowerCase() !== name.toLowerCase()), { name, w, h }];
    setSavedSizes(next);
    try { localStorage.setItem(SAVED_PRINT_SIZES_KEY, JSON.stringify(next)); }
    catch (_) { alert("Could not save this print size in browser storage."); return; }
    setManualName("");
    applySize({ name, w, h });
  };

  const deleteManualSize = (name) => {
    const next = savedSizes.filter((x) => x.name !== name);
    setSavedSizes(next);
    try { localStorage.setItem(SAVED_PRINT_SIZES_KEY, JSON.stringify(next)); }
    catch (_) { alert("Could not update saved print sizes."); }
    if (activeSize === name) setActiveSize("");
  };

  const controlStyle = {
    width: "100%", boxSizing: "border-box", background: "#0f0c08",
    border: "1px solid #382f21", borderRadius: 5, color: "#f1ece1",
    padding: "8px", fontSize: 12, fontFamily: "inherit",
  };

  return (
    <section style={{ background: "#161009", border: "1px solid #2c2418", borderRadius: 8, padding: "0.9rem" }}>
      <Label>Print Item Size · A4 / A5 / A6</Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
        {PRINT_SIZE_PRESETS.map((size) => {
          const fit = calculatePrintFit(sheetW, sheetH, size.w, size.h);
          return (
            <button key={size.name} type="button" onClick={() => applySize(size)} style={{
              background: activeSize === size.name ? "#241c11" : "transparent",
              border: `1px solid ${activeSize === size.name ? "#c9962f" : "#382f21"}`,
              borderRadius: 5, color: "#e0ab45", padding: "8px 4px",
              cursor: "pointer", fontFamily: "inherit", fontSize: 12,
            }}>
              <strong>{size.name}</strong>
              <div style={{ fontSize: 10, color: "#a08f6e", marginTop: 3 }}>{size.w}×{size.h}</div>
              <div style={{ fontSize: 10, color: "#d8cdb4", marginTop: 2 }}>{Math.min(maxCount, fit)} fit</div>
            </button>
          );
        })}
      </div>
      <Hint>Fit count checks both normal and rotated item orientation on the current sheet. Selecting a size updates Images Per Page while preserving the existing grid layouts.</Hint>
      <Label>Manual Print Size (mm)</Label>
      <input value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Size name (e.g. 120×180)" style={{ ...controlStyle, marginBottom: 6 }} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        <input type="number" min="0.1" step="0.1" value={manualW} onChange={(e) => setManualW(e.target.value)} placeholder="Width (mm)" style={controlStyle} />
        <input type="number" min="0.1" step="0.1" value={manualH} onChange={(e) => setManualH(e.target.value)} placeholder="Height (mm)" style={controlStyle} />
      </div>
      <button type="button" onClick={saveManualSize} style={{
        width: "100%", marginTop: 7, padding: "8px", background: "#241c11",
        border: "1px solid #c9962f", borderRadius: 5, color: "#e0ab45",
        cursor: "pointer", fontFamily: "inherit", fontSize: 12,
      }}>Save & Apply Manual Size</button>
      {savedSizes.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <Label>Saved Manual Sizes</Label>
          {savedSizes.map((size) => (
            <div key={size.name} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 5, padding: 6, border: "1px solid #382f21", borderRadius: 5 }}>
              <button type="button" onClick={() => applySize(size)} style={{
                flex: 1, textAlign: "left", background: "transparent", border: 0,
                color: activeSize === size.name ? "#e0ab45" : "#d8cdb4",
                cursor: "pointer", fontFamily: "inherit", fontSize: 11,
              }}>{size.name} · {size.w} × {size.h} mm</button>
              <button type="button" onClick={() => deleteManualSize(size.name)} style={{
                background: "transparent", border: "1px solid #6b3528", borderRadius: 4,
                color: "#e07a5f", padding: "4px 7px", cursor: "pointer", fontSize: 10,
              }}>Delete</button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---- Fractional (0-1) layout templates per images-per-page ----------------
// Identical grid geometry to Pagebind — only the spacing model changes.
const LAYOUTS = {
  1: [{ x: 0, y: 0, w: 1, h: 1 }],

  2: [
    { x: 0,   y: 0, w: 0.5, h: 1 },
    { x: 0.5, y: 0, w: 0.5, h: 1 },
  ],

  3: [
    { x: 0,   y: 0,    w: 1,   h: 0.55 },
    { x: 0,   y: 0.55, w: 0.5, h: 0.45 },
    { x: 0.5, y: 0.55, w: 0.5, h: 0.45 },
  ],

  4: [
    { x: 0,   y: 0,   w: 0.5, h: 0.5 },
    { x: 0.5, y: 0,   w: 0.5, h: 0.5 },
    { x: 0,   y: 0.5, w: 0.5, h: 0.5 },
    { x: 0.5, y: 0.5, w: 0.5, h: 0.5 },
  ],

  5: [
    { x: 0,     y: 0,   w: 0.5,   h: 0.5 },
    { x: 0.5,   y: 0,   w: 0.5,   h: 0.5 },
    { x: 0,     y: 0.5, w: 1 / 3, h: 0.5 },
    { x: 1 / 3, y: 0.5, w: 1 / 3, h: 0.5 },
    { x: 2 / 3, y: 0.5, w: 1 / 3, h: 0.5 },
  ],

  6: [
    { x: 0,     y: 0,   w: 1 / 3, h: 0.5 },
    { x: 1 / 3, y: 0,   w: 1 / 3, h: 0.5 },
    { x: 2 / 3, y: 0,   w: 1 / 3, h: 0.5 },
    { x: 0,     y: 0.5, w: 1 / 3, h: 0.5 },
    { x: 1 / 3, y: 0.5, w: 1 / 3, h: 0.5 },
    { x: 2 / 3, y: 0.5, w: 1 / 3, h: 0.5 },
  ],

  7: [
    { x: 0,     y: 0,   w: 0.25,  h: 0.5 },
    { x: 0.25,  y: 0,   w: 0.25,  h: 0.5 },
    { x: 0.5,   y: 0,   w: 0.25,  h: 0.5 },
    { x: 0.75,  y: 0,   w: 0.25,  h: 0.5 },
    { x: 0,     y: 0.5, w: 1 / 3, h: 0.5 },
    { x: 1 / 3, y: 0.5, w: 1 / 3, h: 0.5 },
    { x: 2 / 3, y: 0.5, w: 1 / 3, h: 0.5 },
  ],

  8: [
    { x: 0,    y: 0,   w: 0.25, h: 0.5 },
    { x: 0.25, y: 0,   w: 0.25, h: 0.5 },
    { x: 0.5,  y: 0,   w: 0.25, h: 0.5 },
    { x: 0.75, y: 0,   w: 0.25, h: 0.5 },
    { x: 0,    y: 0.5, w: 0.25, h: 0.5 },
    { x: 0.25, y: 0.5, w: 0.25, h: 0.5 },
    { x: 0.5,  y: 0.5, w: 0.25, h: 0.5 },
    { x: 0.75, y: 0.5, w: 0.25, h: 0.5 },
  ],
};

// Which slot index is the "spine" boundary for 2-up front/back covers.
// Used only to label slots as Back / Front when perPage === 2.
const COVER_LABELS_2UP = ["Back Cover", "Front Cover"];

// ---- File / image helpers --------------------------------------------------
const readFileAsDataURL = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = (e) => res(e.target.result);
    r.onerror = () => rej(new Error("Read failed"));
    r.readAsDataURL(file);
  });

const loadImageObj = (src) =>
  new Promise((res, rej) => {
    const img = new window.Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
    if (img.complete && img.naturalWidth) res(img);
  });

let jsPDFPromise = null;
function loadJsPDF() {
  if (window.jspdf && window.jspdf.jsPDF) return Promise.resolve(window.jspdf.jsPDF);
  if (!jsPDFPromise) {
    jsPDFPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      s.onload = () => resolve(window.jspdf.jsPDF);
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return jsPDFPromise;
}

// Contain-fit box math with independent common / horizontal / vertical zoom.
function computeSlotDraw(
  natW, natH,
  boxX, boxY, boxW, boxH,
  zoom, zoomX, zoomY,
  offX, offY
) {
  const coverScale = Math.max(boxW / natW, boxH / natH);

  const dw = natW * coverScale * zoom * zoomX;
const dh = natH * coverScale * zoom * zoomY;

  const maxPanX = Math.max(0, (dw - boxW) / 2);
  const maxPanY = Math.max(0, (dh - boxH) / 2);

  const baseX = boxX + (boxW - dw) / 2;
  const baseY = boxY + (boxH - dh) / 2;

  return {
    dw,
    dh,
    drawX: baseX + offX * maxPanX,
    drawY: baseY + offY * maxPanY,
    maxPanX,
    maxPanY,
  };
}

// ---- Slot geometry — adjustable outer margin and internal gaps --------------
// Outside margins are equal on all four edges. Internal gaps are twice the
// selected outer margin; half of each gap is inset from each neighbouring slot.
// Extension is distributed evenly across the distinct grid rows/columns.
function slotRectPx(template, pagePxW, pagePxH, perPage, extensionX = 0, extensionY = 0, outerPx = 0, gapPx = 0) {
  const baseW = Math.max(1, pagePxW - extensionX);
  const baseH = Math.max(1, pagePxH - extensionY);
  const usableW = Math.max(1, baseW - 2 * outerPx);
  const usableH = Math.max(1, baseH - 2 * outerPx);
  const layout = LAYOUTS[perPage] || [];
  const xBounds = [...new Set(layout.flatMap((t) => [t.x, t.x + t.w]))].sort((a, b) => a - b);
  const yBounds = [...new Set(layout.flatMap((t) => [t.y, t.y + t.h]))].sort((a, b) => a - b);
  const xStart = Math.max(0, xBounds.findIndex((v) => Math.abs(v - template.x) < 1e-8));
  const xEnd = Math.max(xStart + 1, xBounds.findIndex((v) => Math.abs(v - (template.x + template.w)) < 1e-8));
  const yStart = Math.max(0, yBounds.findIndex((v) => Math.abs(v - template.y) < 1e-8));
  const yEnd = Math.max(yStart + 1, yBounds.findIndex((v) => Math.abs(v - (template.y + template.h)) < 1e-8));
  const extraPerColumn = xBounds.length > 1 ? extensionX / (xBounds.length - 1) : 0;
  const extraPerRow = yBounds.length > 1 ? extensionY / (yBounds.length - 1) : 0;
  const leftInset = xStart > 0 ? gapPx / 2 : 0;
  const rightInset = xEnd < xBounds.length - 1 ? gapPx / 2 : 0;
  const topInset = yStart > 0 ? gapPx / 2 : 0;
  const bottomInset = yEnd < yBounds.length - 1 ? gapPx / 2 : 0;
  return {
    x: outerPx + template.x * usableW + xStart * extraPerColumn + leftInset,
    y: outerPx + template.y * usableH + yStart * extraPerRow + topInset,
    w: Math.max(1, template.w * usableW + (xEnd - xStart) * extraPerColumn - leftInset - rightInset),
    h: Math.max(1, template.h * usableH + (yEnd - yStart) * extraPerRow - topInset - bottomInset),
  };
}

// Blue cutting-mark settings: short 5 mm ticks at shared grid seams.
const ROW_SPLIT_MARK_COLOR = "#1e3a8a";
const ROW_SPLIT_MARK_RGB = [30, 58, 138];
const ROW_SPLIT_MARK_WIDTH_MM = 0.6;
const EDGE_TICK_LENGTH_MM = 5;


// Short blue cutting ticks at the midpoint of every shared grid seam.
function getGridCutTicks(rects, length = EDGE_TICK_LENGTH_MM) {
  const ticks = [], eps = 0.75, maxGap = 100;
  for (let i = 0; i < rects.length; i++) for (let j = i + 1; j < rects.length; j++) {
    const a = rects[i], b = rects[j];
    const oy = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
    const ox = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
    const cy = Math.max(a.y, b.y) + oy / 2, cx = Math.max(a.x, b.x) + ox / 2;
    const gapRight = b.x - (a.x + a.w), gapLeft = a.x - (b.x + b.w);
    const gapDown = b.y - (a.y + a.h), gapUp = a.y - (b.y + b.h);
    if (oy > eps && gapRight >= -eps && gapRight <= maxGap) {
      const x = (a.x + a.w + b.x) / 2;
      ticks.push({x1:x,y1:cy-length/2,x2:x,y2:cy+length/2});
    } else if (oy > eps && gapLeft >= -eps && gapLeft <= maxGap) {
      const x = (b.x + b.w + a.x) / 2;
      ticks.push({x1:x,y1:cy-length/2,x2:x,y2:cy+length/2});
    } else if (ox > eps && gapDown >= -eps && gapDown <= maxGap) {
      const y = (a.y + a.h + b.y) / 2;
      ticks.push({x1:cx-length/2,y1:y,x2:cx+length/2,y2:y});
    } else if (ox > eps && gapUp >= -eps && gapUp <= maxGap) {
      const y = (b.y + b.h + a.y) / 2;
      ticks.push({x1:cx-length/2,y1:y,x2:cx+length/2,y2:y});
    }
  }
  return ticks;
}
function drawGridCutTicksCanvas(ctx, rects, ppm) {
  ctx.save(); ctx.strokeStyle = ROW_SPLIT_MARK_COLOR; ctx.lineWidth = Math.max(1.25, 0.35 * ppm); ctx.setLineDash([]);
  getGridCutTicks(rects, EDGE_TICK_LENGTH_MM * ppm).forEach(t => { ctx.beginPath(); ctx.moveTo(t.x1,t.y1); ctx.lineTo(t.x2,t.y2); ctx.stroke(); });
  ctx.restore();
}
function drawGridCutTicksPdf(pdf, rects) {
  pdf.setDrawColor(...ROW_SPLIT_MARK_RGB); pdf.setLineWidth(ROW_SPLIT_MARK_WIDTH_MM); pdf.setLineDashPattern([],0);
  getGridCutTicks(rects).forEach(t => pdf.line(t.x1,t.y1,t.x2,t.y2));
}

// ---- Draw one full page onto a canvas -------------------------------------
function renderPage(ctx, pagePxW, pagePxH, perPage, pageImages, selectedId, natSizeCache, extensionX = 0, extensionY = 0, outerPx = 0, gapPx = 0, showCuttingMarks = true, ppm = 3.2) {
  ctx.clearRect(0, 0, pagePxW, pagePxH);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, pagePxW, pagePxH);

  const template = LAYOUTS[perPage];
  template.forEach((t, i) => {
    const rect = slotRectPx(t, pagePxW, pagePxH, perPage, extensionX, extensionY, outerPx, gapPx);
    const entry = pageImages[i];

    if (!entry) {
      ctx.fillStyle = "#f4f1ea";
      ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
      ctx.strokeStyle = "#d8d0bd";
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(rect.x + 1, rect.y + 1, rect.w - 2, rect.h - 2);
      ctx.setLineDash([]);
      ctx.fillStyle = "#a89f8a";
      ctx.font = `${Math.max(10, rect.w * 0.06)}px Georgia, serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("empty", rect.x + rect.w / 2, rect.y + rect.h / 2);
      return;
    }

    const nat = natSizeCache.get(entry.id);
    if (!nat) return;

    ctx.save();
    ctx.beginPath();
    ctx.rect(rect.x, rect.y, rect.w, rect.h);
    ctx.clip();
    const { dw, dh, drawX, drawY } = computeSlotDraw(
      nat.w, nat.h, rect.x, rect.y, rect.w, rect.h,
      entry.zoom, entry.zoomX, entry.zoomY,
      entry.offX, entry.offY
    );
    ctx.drawImage(nat.imgObj, drawX, drawY, dw, dh);
    ctx.restore();

    if (entry.id === selectedId) {
      ctx.strokeStyle = "#c9962f";
      ctx.lineWidth = Math.max(2, rect.w * 0.006);
      ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
    }
    // Note: no faint divider is drawn between non-selected slots on purpose —
    // covers must show zero seam between adjacent photos.
  });
  if (showCuttingMarks) {
    const rects = template.map(t => slotRectPx(t, pagePxW, pagePxH, perPage, extensionX, extensionY, outerPx, gapPx));
    drawGridCutTicksCanvas(ctx, rects, ppm);
  }
}

// ---- Small UI atoms ---------------------------------------------------------
function Label({ children }) {
  return (
    <label style={{
      display: "block", fontSize: 10.5, letterSpacing: "0.14em",
      color: "#a08f6e", marginBottom: 7, textTransform: "uppercase",
      fontFamily: "'Iowan Old Style','Georgia',serif",
    }}>{children}</label>
  );
}

function Hint({ children }) {
  return <p style={{ fontSize: 11, color: "#7a705c", marginTop: 5, lineHeight: 1.5 }}>{children}</p>;
}

function Select({ value, onChange, children }) {
  return (
    <select value={value} onChange={onChange} style={{
      width: "100%", background: "#1a1611", border: "1px solid #382f21",
      borderRadius: 6, color: "#f1ece1", padding: "9px 12px", fontSize: 13,
      cursor: "pointer", fontFamily: "'DM Mono','Fira Mono',monospace",
    }}>{children}</select>
  );
}

function ProgressBar({ value, total, label }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: "#a08f6e" }}>{label}</span>
        <span style={{ fontSize: 11, color: "#c9962f" }}>{value} / {total} ({pct}%)</span>
      </div>
      <div style={{ background: "#241e15", borderRadius: 4, height: 6, overflow: "hidden" }}>
        <div style={{
          width: `${pct}%`, height: "100%",
          background: "linear-gradient(90deg,#a8781e,#e0ab45)",
          transition: "width 0.2s", borderRadius: 4,
        }} />
      </div>
    </div>
  );
}

function Thumbnail({ src, index, selected, onSelect, onRemove }) {
  return (
    <div
      onClick={() => onSelect(index)}
      style={{
        position: "relative", width: 60, height: 60,
        border: `2px solid ${selected ? "#c9962f" : "#2c2418"}`,
        borderRadius: 5, overflow: "hidden", cursor: "pointer",
        flexShrink: 0, background: "#14110c",
      }}
    >
      <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(index); }}
        style={{
          position: "absolute", top: 1, right: 1,
          background: "rgba(0,0,0,0.75)", border: "none",
          borderRadius: 3, color: "#f18b6d", fontSize: 10, lineHeight: 1,
          padding: "1px 3px", cursor: "pointer", fontFamily: "inherit",
        }}
      >×</button>
      <div style={{
        position: "absolute", bottom: 1, left: 2,
        fontSize: 9, color: "rgba(255,255,255,0.55)", userSelect: "none",
      }}>{index + 1}</div>
    </div>
  );
}

function ZoomControl({ label, value, onMinus, onPlus }) {
  const btnStyle = {
    width: 36, height: 32, background: "#1a1611", border: "1px solid #382f21",
    borderRadius: 5, color: "#e0ab45", cursor: "pointer",
    fontFamily: "'DM Mono','Fira Mono',monospace", fontSize: 18, fontWeight: 700, lineHeight: 1,
  };

  return (
    <div style={{ marginTop: 10 }}>
      <Label>{label}</Label>
      <div style={{ display: "grid", gridTemplateColumns: "36px 1fr 36px", gap: 7, alignItems: "center" }}>
        <button type="button" onClick={onMinus} style={btnStyle}>−</button>
        <div style={{
          height: 32, display: "flex", alignItems: "center", justifyContent: "center",
          border: "1px solid #2c2418", borderRadius: 5, background: "#0f0c08",
          color: "#d8cdb4", fontSize: 12,
        }}>{value.toFixed(2)}×</div>
        <button type="button" onClick={onPlus} style={btnStyle}>+</button>
      </div>
    </div>
  );
}

function NavBtn({ onClick, disabled, children }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: disabled ? "transparent" : "#1a1611",
      border: `1px solid ${disabled ? "#241e15" : "#382f21"}`,
      borderRadius: 5, color: disabled ? "#241e15" : "#c4b697",
      padding: "4px 10px", fontSize: 11, cursor: disabled ? "default" : "pointer",
      fontFamily: "'DM Mono','Fira Mono',monospace", transition: "all 0.15s",
    }}>{children}</button>
  );
}

// ============================================================================
// Main component
// ============================================================================
export default function CoverPage() {
  const [pageKey, setPageKey]         = useState("landscape");
  const [perPage, setPerPage]         = useState(2); // 2 = Back Cover + Front Cover, edge to edge
  const [canvasMode, setCanvasMode]   = useState("normal");
  const [extensionDirection, setExtensionDirection] = useState("vertical");
  const [extensionMM, setExtensionMM] = useState(0);
  const [outerMarginMM, setOuterMarginMM] = useState(0);
  const [fillAllGrids, setFillAllGrids] = useState(false);
  const [applyAdjustmentsToAll, setApplyAdjustmentsToAll] = useState(false);
  const [showCuttingMarks, setShowCuttingMarks] = useState(true);

  const [images, setImages]           = useState([]); // {id,name,dataUrl,zoom,zoomX,zoomY,offX,offY}
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedId, setSelectedId]   = useState(null);

  const [isDragging, setIsDragging]   = useState(false); // file drag-over
  const [generating, setGenerating]   = useState(false);
  const [progress, setProgress]       = useState({ done: 0, total: 0 });

  const canvasRef    = useRef(null);
  const fileInputRef = useRef(null);
  const natCache     = useRef(new Map()); // id -> { imgObj, w, h }
  const panState     = useRef(null);      // active drag info

  const { w: basePageW, h: basePageH } = PAGE_SIZES[pageKey];
  const appliedExtensionMM = canvasMode === "extended" ? extensionMM : 0;
  const extensionXMM = extensionDirection === "horizontal" || extensionDirection === "both" ? appliedExtensionMM : 0;
  const extensionYMM = extensionDirection === "vertical" || extensionDirection === "both" ? appliedExtensionMM : 0;
  const pageW = basePageW + extensionXMM;
  const pageH = basePageH + extensionYMM;
  const totalPages = Math.max(1, Math.ceil(images.length / perPage) || 1);

  useEffect(() => {
    if (currentPage > totalPages - 1) setCurrentPage(Math.max(0, totalPages - 1));
  }, [totalPages, currentPage]);

  const pages = useMemo(() => {
    const chunks = [];
    for (let i = 0; i < images.length; i += perPage) chunks.push(images.slice(i, i + perPage));
    if (chunks.length === 0) chunks.push([]);
    return chunks;
  }, [images, perPage]);

  const currentPageImages = pages[currentPage] || [];

  // ---- Load natural sizes for any new images --------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const img of images) {
        if (!natCache.current.has(img.id)) {
          try {
            const obj = await loadImageObj(img.dataUrl);
            if (cancelled) return;
            natCache.current.set(img.id, { imgObj: obj, w: obj.naturalWidth, h: obj.naturalHeight });
            setImages((prev) => [...prev]); // trigger repaint
          } catch (_) { /* ignore broken image */ }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [images]);

  // ---- Preview canvas ---------------------------------------------------------
  const PPM = 3.2;
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pxW = Math.round(pageW * PPM);
    const pxH = Math.round(pageH * PPM);
    canvas.width = pxW;
    canvas.height = pxH;
    const ctx = canvas.getContext("2d");
    renderPage(ctx, pxW, pxH, perPage, currentPageImages, selectedId, natCache.current, Math.round(extensionXMM * PPM), Math.round(extensionYMM * PPM), outerMarginMM * PPM, outerMarginMM * 2 * PPM, showCuttingMarks, PPM);
  }, [pageW, pageH, perPage, currentPageImages, selectedId, images, extensionXMM, extensionYMM, outerMarginMM, showCuttingMarks]);

  // ---- Upload handling ---------------------------------------------------------
  const addFiles = useCallback(async (files) => {
    const imgFiles = Array.from(files).filter((f) => f.type.startsWith("image/")).slice(0, 500);
    const entries = await Promise.all(imgFiles.map(async (f, idx) => ({
      id: `img_${Date.now()}_${idx}_${Math.random().toString(36).slice(2, 7)}`,
      name: f.name,
      dataUrl: await readFileAsDataURL(f),
      zoom: 1, zoomX: 1, zoomY: 1, offX: 0, offY: 0,
    })));
    setImages((prev) => {
      const incoming = fillAllGrids && entries.length === 1
        ? Array.from({ length: Math.max(1, perPage) }, (_, i) => ({ ...entries[0], id: `${entries[0].id}_grid_${i + 1}` }))
        : entries;
      return [...prev, ...incoming].slice(0, 2000);
    });
  }, [fillAllGrids, perPage]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  // ---- Selection + drag-to-pan on the preview canvas ---------------------------
  const findSlotAtPoint = useCallback((px, py) => {
    const pxW = Math.round(pageW * PPM);
    const pxH = Math.round(pageH * PPM);
    const template = LAYOUTS[perPage];
    for (let i = 0; i < template.length; i++) {
      const rect = slotRectPx(template[i], pxW, pxH, perPage, Math.round(extensionXMM * PPM), Math.round(extensionYMM * PPM), outerMarginMM * PPM, outerMarginMM * 2 * PPM);
      if (px >= rect.x && px <= rect.x + rect.w && py >= rect.y && py <= rect.y + rect.h) {
        return { index: i, rect };
      }
    }
    return null;
  }, [pageW, pageH, perPage, extensionXMM, extensionYMM, outerMarginMM]);

  const canvasPointFromEvent = (e) => {
    const canvas = canvasRef.current;
    const bounds = canvas.getBoundingClientRect();
    const scaleX = canvas.width / bounds.width;
    const scaleY = canvas.height / bounds.height;
    return {
      x: (e.clientX - bounds.left) * scaleX,
      y: (e.clientY - bounds.top) * scaleY,
    };
  };

  const onCanvasMouseDown = (e) => {
    const pt = canvasPointFromEvent(e);
    const hit = findSlotAtPoint(pt.x, pt.y);
    if (!hit) return;
    const entry = currentPageImages[hit.index];
    if (!entry) return;
    setSelectedId(entry.id);
    const nat = natCache.current.get(entry.id);
    if (!nat) return;
    const { maxPanX, maxPanY } = computeSlotDraw(
      nat.w, nat.h, hit.rect.x, hit.rect.y, hit.rect.w, hit.rect.h,
      entry.zoom, entry.zoomX, entry.zoomY,
      entry.offX, entry.offY
    );
    panState.current = {
      id: entry.id, startX: pt.x, startY: pt.y,
      startOffX: entry.offX, startOffY: entry.offY,
      maxPanX, maxPanY,
    };
  };

  const onCanvasMouseMove = (e) => {
    if (!panState.current) return;
    const pt = canvasPointFromEvent(e);
    const { id, startX, startY, startOffX, startOffY, maxPanX, maxPanY } = panState.current;
    const dx = pt.x - startX;
    const dy = pt.y - startY;
    const nOffX = maxPanX > 0 ? clamp(startOffX + dx / maxPanX, -1, 1) : 0;
    const nOffY = maxPanY > 0 ? clamp(startOffY + dy / maxPanY, -1, 1) : 0;
    updateImage(id, { offX: nOffX, offY: nOffY });
  };

  const endPan = () => { panState.current = null; };

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  const updateImage = (id, patch) => {
    setImages((prev) => prev.map((img) => (img.id === id ? { ...img, ...patch } : img)));
  };

  const ZOOM_STEP = 0.01;
  const MIN_ZOOM = 0.25;
  const MAX_ZOOM = 4;

  const adjustZoom = (id, key, delta) => {
    setImages((prev) => {
      const selected = prev.find(img => img.id === id);
      if (!selected) return prev;
      const nextValue = clamp(Math.round(((selected[key] ?? 1) + delta) * 100) / 100, MIN_ZOOM, MAX_ZOOM);
      return prev.map(img => img.id === id
        ? { ...img, [key]: nextValue }
        : applyAdjustmentsToAll ? { ...img, [key]: nextValue } : img);
    });
  };

  const selectedImage = images.find((i) => i.id === selectedId) || null;

  // ---- PDF generation -----------------------------------------------------------
  // Export uses the same adjustable margins, gaps, and extension geometry as the preview.
  const handleGenerate = async () => {
    if (images.length === 0) return;

    setGenerating(true);
    setProgress({ done: 0, total: pages.length });
    await new Promise((r) => setTimeout(r, 30));

    try {
      const jsPDF = await loadJsPDF();
      const orientation = pageW > pageH ? "l" : "p";
      const pdf = new jsPDF({
        orientation,
        unit: "mm",
        format: [pageW, pageH],
        compress: true,
      });

      for (let p = 0; p < pages.length; p++) {
        if (p > 0) {
          pdf.addPage([pageW, pageH], orientation);
        }

        const pageImages = pages[p];
        const template = LAYOUTS[perPage];

        for (let i = 0; i < template.length; i++) {
          const entry = pageImages[i];
          if (!entry) continue;

          const nat = natCache.current.get(entry.id);
          if (!nat) continue;

          const rect = slotRectPx(template[i], pageW, pageH, perPage, extensionXMM, extensionYMM, outerMarginMM, outerMarginMM * 2);

          const draw = computeSlotDraw(
            nat.w, nat.h, rect.x, rect.y, rect.w, rect.h,
            entry.zoom, entry.zoomX, entry.zoomY,
            entry.offX, entry.offY
          );

          // Clip to the slot only — never stroke the clip path into the PDF.
          pdf.saveGraphicsState();
          pdf.rect(rect.x, rect.y, rect.w, rect.h, null);
          pdf.clip();
          pdf.discardPath();

          let format = "JPEG";
          if (entry.dataUrl.startsWith("data:image/png")) format = "PNG";
          else if (entry.dataUrl.startsWith("data:image/webp")) format = "WEBP";

          pdf.addImage(
            entry.dataUrl, format,
            draw.drawX, draw.drawY, draw.dw, draw.dh,
            undefined, "FAST"
          );

          pdf.restoreGraphicsState();
        }

        if (showCuttingMarks) {
          const rects = template.map(t => slotRectPx(t, pageW, pageH, perPage, extensionXMM, extensionYMM, outerMarginMM, outerMarginMM * 2));
          drawGridCutTicksPdf(pdf, rects);
        }

        setProgress({ done: p + 1, total: pages.length });
        await new Promise((r) => setTimeout(r, 0));
      }

      pdf.save(`cover_${pages.length}pages_${pageKey}.pdf`);
    } catch (err) {
      console.error(err);
      alert("Failed to generate PDF: " + err.message);
    } finally {
      setGenerating(false);
      setProgress({ done: 0, total: 0 });
    }
  };

  const mmToIn = (mm) => (mm / 25.4).toFixed(1);
  const canGen = images.length > 0 && !generating;

  const slotLabel = (i) => (perPage === 2 ? COVER_LABELS_2UP[i] : `Slot ${i + 1}`);

  // ---- Render -------------------------------------------------------------------
  return (
    <div style={{
      minHeight: "100vh", background: "#0e0b07",
      fontFamily: "'DM Mono','Fira Mono',monospace",
      color: "#f1ece1", display: "flex", flexDirection: "column",
    }}>
      <header style={{
        borderBottom: "1px solid #241e15", padding: "1.25rem 2rem",
        display: "flex", alignItems: "baseline", gap: "1rem",
      }}>
        <span style={{
          fontSize: 11, letterSpacing: "0.2em", color: "#a08f6e",
          textTransform: "uppercase",
        }}>/cover</span>
        <h1 style={{
          fontSize: 21, fontWeight: 400, margin: 0, color: "#f6efe1",
          letterSpacing: "-0.01em", fontFamily: "'Iowan Old Style','Georgia',serif",
        }}>
          CoverPage — Full-Bleed Cover Composer
        </h1>
      </header>

      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "360px 1fr", overflow: "hidden" }}>
        {/* ---------------- Sidebar ---------------- */}
        <aside style={{
          borderRight: "1px solid #241e15", padding: "1.5rem",
          display: "flex", flexDirection: "column", gap: "1.25rem", overflowY: "auto",
        }}>
          {/* Page size */}
          <section>
            <Label>Page Size</Label>
            <Select value={pageKey} onChange={(e) => setPageKey(e.target.value)}>
              {Object.entries(PAGE_SIZES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </Select>
            <Hint>{pageW.toFixed(1)} × {pageH.toFixed(1)} mm ({mmToIn(pageW)} × {mmToIn(pageH)} in)</Hint>
          </section>

          <PrintSizeControls
            sheetW={pageW}
            sheetH={pageH}
            perPage={perPage}
            setPerPage={setPerPage}
            maxCount={8}
          />

          {/* Canvas extension */}
          <section>
            <Label>Canvas Mode</Label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {["normal", "extended"].map((mode) => (
                <button key={mode} type="button" onClick={() => setCanvasMode(mode)} style={{
                  background: canvasMode === mode ? "#241c11" : "transparent",
                  border: `1px solid ${canvasMode === mode ? "#c9962f" : "#2c2418"}`,
                  borderRadius: 6, color: canvasMode === mode ? "#e0ab45" : "#a08f6e",
                  padding: "9px", fontSize: 12, cursor: "pointer", textTransform: "capitalize",
                  fontFamily: "inherit",
                }}>{mode}</button>
              ))}
            </div>
            {canvasMode === "extended" && <>
              <Label>Extension Direction</Label>
              <Select value={extensionDirection} onChange={(e) => setExtensionDirection(e.target.value)}>
                <option value="vertical">Vertical</option>
                <option value="horizontal">Horizontal</option>
                <option value="both">Both</option>
              </Select>
              <Label>Extension Size: {extensionMM.toFixed(1)} mm</Label>
              <div style={{ display: "grid", gridTemplateColumns: "36px 1fr 36px", gap: 7, alignItems: "center" }}>
                <button type="button" onClick={() => setExtensionMM((v) => Math.max(0, Math.round((v - 0.5) * 10) / 10))} style={{ padding: 7, background: "#1a1611", border: "1px solid #382f21", borderRadius: 5, color: "#e0ab45" }}>−</button>
                <input aria-label="Extension size in millimetres" type="range" min="0" max="20" step="0.5" value={extensionMM} onChange={(e) => setExtensionMM(Number(e.target.value))} />
                <button type="button" onClick={() => setExtensionMM((v) => Math.min(20, Math.round((v + 0.5) * 10) / 10))} style={{ padding: 7, background: "#1a1611", border: "1px solid #382f21", borderRadius: 5, color: "#e0ab45" }}>+</button>
              </div>
              <Hint>Extra space is divided evenly across grid rows or columns. For example, an 8 mm vertical extension adds 4 mm to each row in a two-row layout. PDF export uses the extended page size.</Hint>
            </>}
          </section>

          <section style={{ background: "#161009", border: "1px solid #2c2418", borderRadius: 8, padding: "0.9rem" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 11, color: "#d8cdb4" }}>
              <input type="checkbox" checked={showCuttingMarks} onChange={e => setShowCuttingMarks(e.target.checked)} />
              Blue Grid Cutting Marks (Tiny Ticks)
            </label>
            <Hint>Only short blue marks at shared grid boundaries; no full-length lines.</Hint>
          </section>

          {/* Images per page */}
          <section>
            <Label>Images Per Page</Label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <button
                  key={n}
                  onClick={() => setPerPage(n)}
                  style={{
                    width: "100%", background: perPage === n ? "#241c11" : "transparent",
                    border: `1px solid ${perPage === n ? "#c9962f" : "#2c2418"}`,
                    borderRadius: 6, color: perPage === n ? "#e0ab45" : "#a08f6e",
                    padding: "10px 0", fontSize: 14, cursor: "pointer",
                    fontFamily: "inherit", fontWeight: 600, transition: "all 0.15s",
                  }}
                >{n}</button>
              ))}
            </div>
            <Hint>
              {perPage === 2
                ? "2-up: left slot is Back Cover, right slot is Front Cover — touching at the spine."
                : `${pages.length} page${pages.length !== 1 ? "s" : ""} total for ${images.length} image${images.length !== 1 ? "s" : ""}.`}
            </Hint>
          </section>

          {/* Adjustable outer margin and internal photo gaps */}
          <section style={{
            background: "#161009", border: "1px solid #2c2418", borderRadius: 8, padding: "0.9rem",
          }}>
            <p style={{
              fontSize: 10.5, letterSpacing: "0.14em", color: "#a08f6e",
              textTransform: "uppercase", margin: "0 0 8px",
              fontFamily: "'Iowan Old Style','Georgia',serif",
            }}>Space Adjustment</p>
            <p style={{ fontSize: 12, color: "#e0ab45", margin: "0 0 8px", fontWeight: 600 }}>
              Outside margin: {outerMarginMM.toFixed(1)} mm · Internal gaps: {(outerMarginMM * 2).toFixed(1)} mm
            </p>
            <input
              type="range" min="0" max="30" step="0.5" value={outerMarginMM}
              aria-label="Outside margin and photo gap in millimetres"
              onChange={(e) => setOuterMarginMM(Number(e.target.value))}
              style={{ width: "100%", accentColor: "#c9962f" }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#7a705c" }}>
              <span>0 mm — full bleed</span><span>30 mm</span>
            </div>
            <Hint>Adjusts all four outside margins equally. Internal horizontal and vertical gaps are twice the selected margin. Set to 0 mm for the original edge-to-edge cover. Preview and exported PDF use the same measurements.</Hint>
          </section>

          {/* Selected image adjust */}
          <section style={{
            background: "#161009", border: "1px solid #2c2418", borderRadius: 8, padding: "0.9rem",
          }}>
            <p style={{
              fontSize: 10.5, color: "#a08f6e", margin: "0 0 8px", textTransform: "uppercase",
              letterSpacing: "0.12em", fontFamily: "'Iowan Old Style','Georgia',serif",
            }}>Adjust Selected Photo</p>

            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, cursor: "pointer", fontSize: 11, color: "#d8cdb4" }}>
              <input type="checkbox" checked={applyAdjustmentsToAll} onChange={e => setApplyAdjustmentsToAll(e.target.checked)} />
              Apply width / height adjustments to all images
            </label>
            {selectedImage ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <img src={selectedImage.dataUrl} alt="" style={{
                    width: 40, height: 40, objectFit: "cover", borderRadius: 4,
                    border: "1px solid #382f21",
                  }} />
                  <span style={{ fontSize: 11, color: "#d8cdb4", wordBreak: "break-all" }}>
                    {selectedImage.name}
                  </span>
                </div>

                <ZoomControl
                  label="Common Zoom"
                  value={selectedImage.zoom}
                  onMinus={() => adjustZoom(selectedImage.id, "zoom", -ZOOM_STEP)}
                  onPlus={() => adjustZoom(selectedImage.id, "zoom", ZOOM_STEP)}
                />
                <Hint>Changes width and height equally in 0.01× steps.</Hint>

                <ZoomControl
                  label="Horizontal Zoom"
                  value={selectedImage.zoomX}
                  onMinus={() => adjustZoom(selectedImage.id, "zoomX", -ZOOM_STEP)}
                  onPlus={() => adjustZoom(selectedImage.id, "zoomX", ZOOM_STEP)}
                />
                <Hint>Changes only the image width in 0.01× steps.</Hint>

                <ZoomControl
                  label="Vertical Zoom"
                  value={selectedImage.zoomY}
                  onMinus={() => adjustZoom(selectedImage.id, "zoomY", -ZOOM_STEP)}
                  onPlus={() => adjustZoom(selectedImage.id, "zoomY", ZOOM_STEP)}
                />
                <Hint>Changes only the image height in 0.01× steps.</Hint>

                <button
                  onClick={() => updateImage(selectedImage.id, {
                    zoom: 1, zoomX: 1, zoomY: 1, offX: 0, offY: 0,
                  })}
                  style={{
                    marginTop: 8, width: "100%", background: "transparent",
                    border: "1px solid #382f21", borderRadius: 5,
                    color: "#a08f6e", padding: "7px 10px", fontSize: 11,
                    cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  Reset zoom & position
                </button>

                <Hint>
                  Use + / − to make the selected image use more or less slot space.
                  Enlarged images stay clipped to their own slot and cannot overlap neighbouring images.
                  Drag the image on the preview to reposition cropped areas.
                </Hint>
              </>
            ) : (
              <Hint>
                Click a photo on the page preview or its thumbnail to select it,
                then use Common, Horizontal or Vertical zoom.
              </Hint>
            )}
          </section>

          {/* Upload */}
          <section>
            <Label>Upload Images</Label>
            <div
              onClick={() => fileInputRef.current.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              style={{
                border: `1.5px dashed ${isDragging ? "#c9962f" : images.length ? "#4a7a4f" : "#382f21"}`,
                borderRadius: 8, padding: "1.1rem", textAlign: "center", cursor: "pointer",
                transition: "all 0.2s", background: isDragging ? "#1c1408" : "transparent",
              }}
            >
              {images.length > 0 ? (
                <>
                  <p style={{ fontSize: 12, color: "#7fb583", margin: "0 0 4px" }}>
                    {images.length} image{images.length > 1 ? "s" : ""} loaded
                  </p>
                  <p style={{ fontSize: 11, color: "#7a705c", margin: 0 }}>Drop more or click to add</p>
                </>
              ) : (
                <>
                  <p style={{ fontSize: 22, margin: "0 0 8px", color: "#382f21" }}>↑</p>
                  <p style={{ fontSize: 13, color: "#7a705c", margin: 0 }}>
                    Drop images or click to browse
                  </p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef} type="file" accept="image/*" multiple
              onChange={(e) => addFiles(e.target.files)}
              style={{ display: "none" }}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, cursor: "pointer", fontSize: 11, color: "#d8cdb4" }}>
              <input type="checkbox" checked={fillAllGrids} onChange={e => setFillAllGrids(e.target.checked)} />
              Fill all grids with one uploaded image
            </label>

            {images.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{
                  display: "flex", gap: 6, flexWrap: "wrap",
                  maxHeight: 140, overflowY: "auto", padding: "4px 2px",
                }}>
                  {images.map((img, i) => (
                    <Thumbnail
                      key={img.id} src={img.dataUrl} index={i}
                      selected={img.id === selectedId}
                      onSelect={() => {
                        setSelectedId(img.id);
                        setCurrentPage(Math.floor(i / perPage));
                      }}
                      onRemove={(idx) => {
                        setImages((prev) => prev.filter((_, j) => j !== idx));
                        if (images[idx].id === selectedId) setSelectedId(null);
                      }}
                    />
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                  <p style={{ fontSize: 11, color: "#7a705c", margin: 0 }}>
                    {images.length} image{images.length !== 1 ? "s" : ""} · {pages.length} page{pages.length !== 1 ? "s" : ""}
                  </p>
                  <button
                    onClick={() => { setImages([]); setSelectedId(null); natCache.current.clear(); }}
                    style={{
                      background: "transparent", border: "none", color: "#e07a5f",
                      fontSize: 11, cursor: "pointer", fontFamily: "inherit", padding: 0,
                    }}
                  >Clear all</button>
                </div>
              </div>
            )}

            {generating && progress.total > 0 && (
              <ProgressBar value={progress.done} total={progress.total} label="Rendering pages…" />
            )}
          </section>

          {/* Generate */}
          <button
            onClick={handleGenerate}
            disabled={!canGen}
            style={{
              background: canGen ? "#c9962f" : "#241e15",
              border: "none", borderRadius: 8,
              color: canGen ? "#150f08" : "#4a4030",
              padding: "13px", fontSize: 14, fontWeight: 700,
              cursor: canGen ? "pointer" : "not-allowed",
              fontFamily: "inherit", letterSpacing: "0.04em", transition: "all 0.2s",
            }}
          >
            {generating
              ? `Generating… (${progress.done}/${progress.total})`
              : images.length === 0
              ? "Upload images first"
              : `Download PDF (${pages.length} page${pages.length !== 1 ? "s" : ""})`}
          </button>
        </aside>

        {/* ---------------- Preview ---------------- */}
        <main style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "2rem", background: "#080603", overflow: "auto",
        }}>
          <div style={{ textAlign: "center" }}>
            <p style={{
              fontSize: 11, color: "#4a4030", marginBottom: "0.9rem",
              letterSpacing: "0.12em", textTransform: "uppercase",
            }}>
              Page {currentPage + 1} of {pages.length} — {pageW.toFixed(0)} × {pageH.toFixed(0)} mm
              {perPage === 2 ? ` — ${COVER_LABELS_2UP.join(" / ")}` : ""}
            </p>

            {pages.length > 1 && (
              <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 12 }}>
                <NavBtn onClick={() => setCurrentPage((p) => Math.max(0, p - 1))} disabled={currentPage === 0}>◀ Prev</NavBtn>
                <span style={{ fontSize: 12, color: "#7a705c", lineHeight: "30px" }}>
                  {currentPage + 1} / {pages.length}
                </span>
                <NavBtn onClick={() => setCurrentPage((p) => Math.min(pages.length - 1, p + 1))} disabled={currentPage === pages.length - 1}>Next ▶</NavBtn>
              </div>
            )}

            <div style={{
              boxShadow: "0 0 0 1px #241e15, 0 24px 70px rgba(0,0,0,0.85)",
              display: "inline-block", borderRadius: 3, overflow: "hidden",
            }}>
              <canvas
                ref={canvasRef}
                onMouseDown={onCanvasMouseDown}
                onMouseMove={onCanvasMouseMove}
                onMouseUp={endPan}
                onMouseLeave={endPan}
                style={{
                  display: "block", cursor: currentPageImages.length ? "grab" : "default",
                  maxWidth: "min(760px, 100%)", maxHeight: "72vh",
                  width: "auto", height: "auto",
                }}
              />
            </div>

            {images.length === 0 ? (
              <p style={{ fontSize: 12, color: "#382f21", marginTop: "1rem" }}>
                Upload photos to start composing your cover. Adjust the outside margin and internal gaps as needed.
              </p>
            ) : (
              <p style={{ fontSize: 11, color: "#4a4030", marginTop: "0.9rem" }}>
                Select a photo · use + / − Common, Horizontal or Vertical zoom · drag to reposition
              </p>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
